import axios from 'axios';
import Immutable from 'immutable';
import cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import { communicationStatus, contactMethods } from '~client/constants';
import { authActionTypes } from '~client/constants/actionTypes';
import { cookieNames } from '~client/constants/storage';
import { defaultCommunicationObject, updateCommunicationObject } from '~client/reducers/shared';
import { fireCustomEvent } from '~helpers/custom-events';
import { getCookieDomain } from '~helpers/login-utils';

const token = cookies.get(cookieNames.token);

export const defaultState = Immutable.fromJS({
  base: {
    email: null,
    companyID: '',
    companyName: '',
    token: token || '',
    accountDifferentiation: '',
    role: cookies.get(cookieNames.role) || '',
    uuid: token && jwtDecode(token)?.uuid,
    isLongtail: '',
    reconciliationOption: '',
    status: '',
  },
  login: {
    communication: defaultCommunicationObject,
  },
  resetPassword: {
    emailSent: false,
    communication: defaultCommunicationObject,
  },
  setPassword: {
    communication: defaultCommunicationObject,
  },
  contacts: {
    emails: ['team-supply@student.com'],
    phones: ['44 (0) 203 817 0050'],
  },
  properties: {
    hasManualProperties: null,
    hasHerculesProperties: null,
    hasYugoProperties: null,
    hasManualBookings: null,
  },
});

const processLoggedIn = (state, action) => {
  const roles = jwtDecode(action.token).roles.toString();
  cookies.set(cookieNames.token, action.token, {
    path: '/',
    expires: 2,
    domain: getCookieDomain(),
  });

  cookies.set(cookieNames.role, roles, {
    path: '/',
    expires: 2,
    domain: getCookieDomain(),
  });

  axios.defaults.headers.common.Authorization = `Bearer ${action.token}`;

  // Connect to message center after login.
  fireCustomEvent('connectMsgCenter');

  return state.mergeDeep({
    base: {
      email: action.email,
      token: action.token,
      role: roles,
    },
    login: {
      communication: updateCommunicationObject(communicationStatus.IDLE),
    },
    setPassword: {
      communication: updateCommunicationObject(communicationStatus.IDLE),
    },
  });
};

const authReducer = (state = defaultState, action) => {
  switch (action.type) {
    case authActionTypes.INITIALIZE:
      return state.mergeDeep({
        [action.application]: defaultState.toJS()[action.application],
      });

    case authActionTypes.UPDATE_COMMUNICATION_STATUS:
      return state.mergeDeep({
        [action.application]: {
          communication: updateCommunicationObject(action.status, action.error),
        },
      });

    case authActionTypes.LOGIN_IN_CS:
      return state.mergeDeep({
        login: {
          communication: updateCommunicationObject(action.status, action.error),
        },
      });

    case authActionTypes.LOGGED_IN:
      return processLoggedIn(state, action);

    case authActionTypes.EMAIL_SENT:
      return state.mergeDeep({
        resetPassword: {
          emailSent: true,
        },
      });

    case authActionTypes.LOGOUT:
      cookies.remove(cookieNames.token, { domain: getCookieDomain() });
      cookies.remove(cookieNames.role, { domain: getCookieDomain() });
      cookies.remove(cookieNames.level, { domain: getCookieDomain() });
      cookies.remove(cookieNames.landlordName, { domain: getCookieDomain() });
      cookies.remove(cookieNames.landlordContact, { domain: getCookieDomain() });

      fireCustomEvent('msgCenterUserLogout');
      return state.mergeDeep({
        base: {
          token: '',
          email: null,
          role: '',
          isLongtail: '',
        },
      });

    case authActionTypes.EMAIL_RESET_SUCCESSFUL:
      return state.mergeDeep({
        resetPassword: {
          emailSent: true,
          communication: updateCommunicationObject(communicationStatus.IDLE),
        },
      });

    case authActionTypes.SET_CONTACTS: {
      const country = action.userClientInfo.country && action.userClientInfo.country.toLowerCase();
      if (['au', 'us', 'hk'].indexOf(country) !== -1) {
        return state.mergeDeep({
          contacts: contactMethods[country],
        });
      }
      return state;
    }

    case authActionTypes.SET_LANDLORD_TYPE:
      if (action.payload?.viewer?.accountLevel) {
        cookies.set(cookieNames.level, action.payload.viewer.accountLevel, {
          path: '/',
          expires: 2,
          domain: getCookieDomain(),
        });
      }

      if (action.payload?.viewer?.company?.slug) {
        cookies.set(cookieNames.landlordName, action.payload.viewer.company?.name, {
          path: '/',
          expires: 2,
          domain: getCookieDomain(),
        });
      }

      if (action.payload?.viewer?.company?.slug) {
        cookies.set(cookieNames.landlordSlug, action.payload.viewer.company?.slug, {
          path: '/',
          expires: 2,
          domain: getCookieDomain(),
        });
      }

      return state.mergeDeep({
        base: {
          companyID: action.payload.viewer.company.id,
          companyName: action.payload.viewer.company.name,
          isLongtail: action.payload.viewer.company.isLongtail,
          partnerCountry: action.payload.viewer.company.billingCountry?.slug,
          reconciliationOption: action.payload.viewer.company.reconciliationOption,
          allowNoPayment: action.payload.viewer.company.allowNoPayment,
          status: action.payload.viewer.company.status,
          accountLevel: action.payload.viewer.accountLevel,
          accountDifferentiation: action.payload.viewer.company.accountDifferentiation,
        },
      });
    case authActionTypes.SET_LANDLORD_PROPERTIES:
      return state.mergeDeep({
        properties: {
          hasManualProperties: action.payload.manualProperties.totalCount > 0,
          hasHerculesProperties: action.payload.herculesProperties.totalCount > 0,
          hasYugoProperties: action.payload.yugoProperties.totalCount > 0,
          hasInstantBookProperties: action.payload.instantProperties.totalCount > 0,
          onlyHasLeadProperties:
            action.payload.leadProperties.totalCount > 0 &&
            action.payload.leadProperties.totalCount === action.payload.allProperties.totalCount,
          hasManualBookings: action.payload.company?.bookings?.totalCount > 0,
          hasInstantBookings: action.payload.company?.instantBookings?.totalCount > 0,
          hasTotalOrders: action.payload.company?.totalOrders?.totalCount > 0,
          hasNoPaymentHerculesBookings:
            action.payload.company?.herculesNoPaymentBookings?.totalCount > 0,
          hasNoPaymentInstantBookings:
            action.payload.company?.instantNoPaymentBookings?.totalCount > 0,
          hasLeadOrders: action.payload.company?.leadGenAllOrders?.totalCount > 0,
          hasIntegratedProperty: action.payload.company?.hasIntegratedProperty,
        },
      });
    case authActionTypes.SET_LANDLORD_NAME:
      return state.mergeDeep({
        base: {
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
        },
      });
    default:
      return state;
  }
};

export default authReducer;
